import { REPORT_NOTE_MAX, REPORT_REASON_LABELS, REPORT_REASONS, type ReportReason, type ReportTarget } from '@wyn/contracts';
import { useRef, useState } from 'react';
import { UI } from '../icons/ui-icons.ts';
import { useBlock, useReport } from '../lib/queries.ts';
import { useMe } from '../lib/useMe.ts';
import { Icon } from './Icon.tsx';
import { ErrorNotice } from './Notice.tsx';
import { Callout } from './ui.tsx';

/**
 * The flag beside a line of chat or a player's name. It opens a form asking what was wrong, and once
 * sent, says only that a steward will look into it: the reporter hears nothing more, and the reported
 * player is never told who reported them.
 */
export function ReportButton({ target, what, who, className = '' }: { target: ReportTarget; what: string; who?: string; className?: string }) {
  const dialog = useRef<HTMLDialogElement>(null);
  const report = useReport();
  const block = useBlock();
  const me = useMe();
  // Once reported, the player can also block whoever it was: by name for a player, the speaker for a line.
  const name = who ?? (target.kind === 'player' ? target.name : null);
  const blocked = !!name && !!me.data?.blocks.some((b) => b.name.toLowerCase() === name.toLowerCase());
  const [reason, setReason] = useState<ReportReason | ''>('');
  const [note, setNote] = useState('');
  const open = () => {
    report.reset();
    block.reset();
    setReason('');
    setNote('');
    dialog.current?.showModal();
  };
  const close = () => dialog.current?.close();

  return (
    <>
      <button type="button" className={`text-ink-faint hover:text-blood focus-visible:text-blood ${className}`} aria-label={`Report ${what}`} title="Report to a steward" onClick={open}>
        <Icon name={UI.report} />
      </button>
      <dialog ref={dialog} aria-label={`Report ${what}`} className="panel m-auto w-[min(32rem,calc(100vw-2rem))] text-ink backdrop:bg-vellum/80">
        {report.isSuccess ? (
          <div className="space-y-3">
            <Callout tone="good" role="status">Thank you. A steward will look into it.</Callout>
            {name && (block.isSuccess || blocked ? (
              <p className="text-sm text-ink-dim">You have blocked {name}. You will not see what they say or their offers, and they are not told.</p>
            ) : (
              <p className="text-sm text-ink-dim">
                You can also block {name}: you will not see what they say or their offers, and they are not told.{' '}
                <button type="button" className="btn-quiet py-0 text-sm" disabled={block.isPending} onClick={() => block.mutate({ name })}>Block {name}</button>
              </p>
            ))}
            <ErrorNotice error={block.error} />
            <button type="button" className="btn" onClick={close} autoFocus>Close</button>
          </div>
        ) : (
          <form
            className="space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              if (reason) report.mutate({ target, reason, ...(note.trim() ? { note: note.trim() } : {}) });
            }}
          >
            <h2 className="heading"><Icon name={UI.report} className="mr-1" />Report {what}</h2>
            <fieldset className="space-y-1">
              <legend className="mb-1 text-sm text-ink-dim">What was wrong?</legend>
              {REPORT_REASONS.map((r) => (
                <label key={r} className="flex items-center gap-2 text-sm">
                  <input type="radio" name="reason" value={r} checked={reason === r} onChange={() => setReason(r)} />
                  {REPORT_REASON_LABELS[r]}
                </label>
              ))}
            </fieldset>
            <label className="block space-y-1 text-sm">
              <span className="text-ink-dim">Anything a steward should know? (optional)</span>
              <textarea className="field block h-24 w-full resize-none" maxLength={REPORT_NOTE_MAX} value={note} onChange={(e) => setNote(e.target.value)} />
            </label>
            <p className="text-xs text-ink-faint">
              A steward will see {target.kind === 'chat' ? 'the line and the talk around it, ' : ''}your note and who sent the report. The other player is
              never told who reported them.
            </p>
            <ErrorNotice error={report.error} />
            <div className="flex gap-2">
              <button type="submit" className="btn" disabled={!reason || report.isPending}>Send report</button>
              <button type="button" className="btn-quiet" onClick={close}>Cancel</button>
            </div>
          </form>
        )}
      </dialog>
    </>
  );
}
